import React, { useEffect, useState } from 'react';
import { Rnd } from "react-rnd";

function GlobalDraggableDialog(props: any) {
    const { children, className, onClose } = props;
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [size, setSize] = useState({ width: 600, height: 700 });
    const [isVisible, setIsVisible] = useState(false);

    // Function to determine screen size category
    const getScreenCategory = () => {
        const width = window.innerWidth;
        if (width >= 1440) return "large"; // Large desktop
        if (width >= 1024) return "desktop"; // Standard desktop/small laptop
        if (width >= 768) return "tablet"; // Tablet/small laptop
        return "mobile"; // Mobile devices
    };

    // Calculate proper dimensions based on screen size
    const calculateDimensions = () => {
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        const screenCategory = getScreenCategory();

        // Set width based on screen category
        let modalWidth;
        switch (screenCategory) {
            case "large":
                modalWidth = 600; // Increased from 600
                break;
            case "desktop":
                modalWidth = Math.min(600, screenWidth * 0.45); // Increased from 500, 40% to 45%
                break;
            case "tablet":
                modalWidth = Math.min(500, screenWidth * 0.65); // Increased from 450, 60% to 65%
                break;
            case "mobile":
                modalWidth = screenWidth * 0.97; // Increased from 0.95
                break;
            default:
                modalWidth = 600; // Increased from 500
        }

        // Set height (proportional to width but considering screen height)
        const modalHeight = Math.min(
            Math.max(modalWidth * 1.2, 500), // Increased from 1.1 and 400
            screenHeight * 0.85 // Cap at 85% of screen height
        );

        // Position the modal on the right side with padding
        const paddingRight = 20; // 20px annotation from the right edge
        const modalX = screenWidth - modalWidth - paddingRight; // Align to the right with padding
        const modalY = Math.max(0, (screenHeight - modalHeight) / 2); // Center vertically

        // Ensure modalX doesn't go negative (in case modalWidth is too large)
        const adjustedModalX = Math.max(0, modalX);

        return {
            position: { x: adjustedModalX, y: modalY },
            size: { width: modalWidth, height: modalHeight }
        };
    };

    // Initialize and handle resize
    useEffect(() => {
        const updateDimensions = () => {
            const { position, size } = calculateDimensions();
            setSize(size);
            setPosition(position);
        };

        // Initial calculation
        updateDimensions();
        // Set visible after initial calculation
        setIsVisible(true);

        // Update on resize
        window.addEventListener('resize', updateDimensions);
        return () => window.removeEventListener('resize', updateDimensions);
    }, []);

    // Prevent dragging the modal completely off-screen
    const handleDragStop = (e: any, d: any) => {
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        const newX = Math.max(0, Math.min(d.x, screenWidth - size.width)); // Keep within horizontal bounds
        const newY = Math.max(0, Math.min(d.y, screenHeight - size.height)); // Keep within vertical bounds
        setPosition({ x: newX, y: newY });
    };

    // Ensure resized modal stays within screen bounds
    const handleResize = (e: any, direction: any, ref: any, delta: any, position: any) => {
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        const newWidth = Math.min(ref.offsetWidth, screenWidth * 0.95); // Limit width to 95% of screen
        const newHeight = Math.min(ref.offsetHeight, screenHeight * 0.95); // Limit height to 95% of screen
        const newX = Math.max(0, Math.min(position.x, screenWidth - newWidth)); // Adjust position if needed
        const newY = Math.max(0, Math.min(position.y, screenHeight - newHeight));

        setSize({ width: newWidth, height: newHeight });
        setPosition({ x: newX, y: newY });
    };

    if (!isVisible) return null;

    return (
        <div className="modal-backdrop">
            <Rnd
                position={position}
                size={size}
                onDragStop={handleDragStop}
                onResize={handleResize}
                bounds="window"
                className={`modal-content ${className || ''}`}
                minWidth={300}
                minHeight={400}
                style={{
                    background: "#fff",
                    borderRadius: "8px",
                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                    padding: "20px",
                }}
            >
                {children}
            </Rnd>
        </div>
    );
}

// Inline styles for the backdrop
const styles = `
.modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1000;
}
`;

// Inject styles into the document
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default GlobalDraggableDialog;